/**
 * Blocks drawn by a model rather than by the faces of a cube.
 *
 * "Заміни текстуру скрині на об'єкт." The скриня keeps its cell - it is
 * solid, it holds its twenty-seven stacks, it faces one of four ways - but
 * the mesher leaves it out (see `render: "model"` in the registry) and the
 * scene stands a model in the cell instead: a box of twelve triangles made
 * from Pavlo's picture ("створи скриню 2 з цього малюнку"). Meshy's chest
 * was tried beside it and dropped - smeared at 8k triangles, and heavier
 * both to fetch and to draw ("залиш малюнок, модель скрині видали").
 *
 * One model loaded, one material shared, and a clone per chest standing in
 * a chunk that is on screen. Unlit, tinted with the sky like every body in
 * the game, so a chest at night is not a lamp.
 */

import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { isChest, CHEST_BASE } from "../core/block/registry.js";

const CHEST_URL = "/models/chest-picture.glb";

/** How much of a cell a chest fills across, the way a block chest does. */
const CHEST_SPAN = 0.875;

/** Which way a facing points the front: 0 -Z, 1 +X, 2 +Z, 3 -X. Models face +Z. */
const TURN = [Math.PI, Math.PI / 2, 0, -Math.PI / 2];

export class BlockModels {
  /** The loaded chest, stood a block's way, or null until it arrives. */
  private chest: THREE.Group | null = null;
  private readonly material = new THREE.MeshBasicMaterial({ color: 0xffffff });
  /** What stands in each chunk, by the chunk's key. */
  private readonly byChunk = new Map<string, THREE.Group>();
  /** Chunks to look through again once the model is here. */
  private readonly waiting = new Set<string>();
  generation = 0;

  constructor(private readonly scene: THREE.Scene) {
    this.load();
  }

  private load(): void {
    new GLTFLoader().load(CHEST_URL, (gltf) => {
      this.chest = this.stand(gltf.scene);
      this.generation++;
      // Everything drawn with the old one is drawn again.
      for (const key of [...this.byChunk.keys()]) this.waiting.add(key);
    }, undefined, () => { /* keeps whatever it had */ });
  }

  /** Scaled to a block's chest, standing on the floor of its cell. */
  private stand(scene: THREE.Group): THREE.Group {
    let map: THREE.Texture | null = null;
    scene.traverse((o) => {
      if (!(o instanceof THREE.Mesh)) return;
      const was = o.material as THREE.MeshStandardMaterial;
      map ??= was.map ?? null;
      o.material = this.material;
    });
    if (map !== null) {
      const texture = map as THREE.Texture;
      texture.magFilter = THREE.NearestFilter;
      texture.minFilter = THREE.NearestFilter;
      texture.generateMipmaps = false;
      texture.needsUpdate = true;
      this.material.map = texture;
      this.material.needsUpdate = true;
    }
    const group = new THREE.Group();
    group.add(scene);
    const box = new THREE.Box3().setFromObject(group);
    const size = box.getSize(new THREE.Vector3());
    const scale = CHEST_SPAN / Math.max(size.x, size.z);
    scene.scale.setScalar(scale);
    group.updateMatrixWorld(true);
    const scaled = new THREE.Box3().setFromObject(group);
    const middle = scaled.getCenter(new THREE.Vector3());
    scene.position.set(-middle.x, -scaled.min.y, -middle.z);
    return group;
  }

  /** The sky's colour on everything, once a frame. */
  setTint(color: THREE.Color): void {
    this.material.color.copy(color);
  }

  /**
   * Look through one chunk and stand a chest in every chest cell of it.
   * Called when the chunk changes; cheap - four thousand reads.
   */
  syncChunk(
    key: string, x0: number, y0: number, z0: number, size: number,
    blockAt: (x: number, y: number, z: number) => number,
  ): void {
    this.dropChunk(key);
    if (this.chest === null) { this.waiting.add(key); this.pending.set(key, { x0, y0, z0, size, blockAt }); return; }
    const group = new THREE.Group();
    for (let y = y0; y < y0 + size; y++) {
      for (let z = z0; z < z0 + size; z++) {
        for (let x = x0; x < x0 + size; x++) {
          const id = blockAt(x, y, z);
          if (!isChest(id)) continue;
          const one = this.chest.clone();
          one.position.set(x + 0.5, y, z + 0.5);
          one.rotation.y = TURN[id - CHEST_BASE] ?? 0;
          one.matrixAutoUpdate = false;
          one.updateMatrix();
          group.add(one);
        }
      }
    }
    if (group.children.length === 0) return;
    this.scene.add(group);
    this.byChunk.set(key, group);
  }

  /** How each waiting chunk was asked for, to ask again when the model comes. */
  private readonly pending = new Map<string, {
    x0: number; y0: number; z0: number; size: number;
    blockAt: (x: number, y: number, z: number) => number;
  }>();

  /** Chunks that were asked for before the model had arrived. */
  flushWaiting(blockAt: (x: number, y: number, z: number) => number, size: number): void {
    if (this.chest === null || this.waiting.size === 0) return;
    for (const key of [...this.waiting]) {
      this.waiting.delete(key);
      const had = this.pending.get(key);
      this.pending.delete(key);
      const [cx, cy, cz] = key.split(",").map(Number) as [number, number, number];
      this.syncChunk(key, had?.x0 ?? cx * size, had?.y0 ?? cy * size, had?.z0 ?? cz * size, size, blockAt);
    }
  }

  dropChunk(key: string): void {
    const group = this.byChunk.get(key);
    if (group === undefined) return;
    this.scene.remove(group);
    this.byChunk.delete(key);
  }

  clear(): void {
    for (const key of [...this.byChunk.keys()]) this.dropChunk(key);
    this.waiting.clear();
    this.pending.clear();
  }

  /** How many chests are standing, for the stage and the checks. */
  get count(): number {
    let n = 0;
    for (const g of this.byChunk.values()) n += g.children.length;
    return n;
  }
}
